import React from "react";
import { Eye, EyeOff } from "lucide-react";
import { cn, type ClassValue } from "@kkp/app-kit";

export type PasswordInputProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "size" | "color" | "type"
> & {
  containerClassName?: string;
  className?: string;
};

/**
 * Password input primitive: mirrors the styling of `Input` (`.kkp-input`)
 * and adds a real click-target eye toggle to flip between masked/visible.
 * Internal state only; no external controlled mode for visibility.
 */
export const PasswordInput = React.forwardRef<
  HTMLInputElement,
  PasswordInputProps
>(function PasswordInput(
  { containerClassName, className, disabled, ...props },
  ref,
) {
  const [visible, setVisible] = React.useState(false);

  const base: ClassValue = [
    "w-full",
    "px-3 py-2.5",
    "pr-10",
    "rounded-xl",
    "border",
    "border-[color:rgba(255,255,255,0.12)]",
    "bg-[color:rgba(255,255,255,0.06)]",
    "text-[color:rgba(255,255,255,0.92)]",
    "placeholder:text-[color:rgba(255,255,255,0.35)]",
    "outline-none",
    "transition",
    "focus:border-[color:color-mix(in_oklab,var(--kkp-magenta)_70%,white)]",
    "focus:shadow-[0_0_0_3px_rgba(203,72,226,0.18)]",
    "disabled:cursor-not-allowed disabled:opacity-60",
  ];

  return (
    <div className={cn("relative", containerClassName)}>
      <input
        ref={ref}
        type={visible ? "text" : "password"}
        disabled={disabled}
        className={cn(base, className)}
        {...props}
      />
      <button
        type="button"
        onClick={() => setVisible((v) => !v)}
        disabled={disabled}
        aria-label={visible ? "Hide password" : "Show password"}
        aria-pressed={visible}
        tabIndex={-1}
        className={cn(
          "absolute right-2 top-1/2 -translate-y-1/2",
          "flex h-7 w-7 items-center justify-center rounded-md",
          "text-[color:var(--kkp-text-muted)] hover:text-white",
          "focus-visible:outline-none focus-visible:ring-2",
          "focus-visible:ring-[color:color-mix(in_oklab,var(--kkp-magenta)_70%,white)]",
          "disabled:cursor-not-allowed disabled:opacity-60",
        )}
      >
        {visible ? (
          <EyeOff size={16} aria-hidden="true" />
        ) : (
          <Eye size={16} aria-hidden="true" />
        )}
      </button>
    </div>
  );
});
